/**
 * InvestorEmailsTab
 *
 * Staff view of the business emails sent to one investor (drawdown notices,
 * distributions, ad-hoc fund emails) with delivery status - the proof trail for
 * the defaulting-investor process. Clicking a row opens the rendered email body.
 */

import { useState } from 'react';
import { Clock, Loader2, Mail, Paperclip } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { cn } from '@/lib/utils';
import { DeliveryStatusBadge } from '@/components/email/DeliveryStatusBadge';
import { EmailAttachments } from '@/components/email/EmailAttachments';
import { TransactionPagination } from '@/components/general-ledger/TransactionPagination';
import {
  useInvestorEmails,
  useInvestorEmail,
} from '@/hooks/react-query/useInvestors';
import { formatDateTime, formatReferenceType } from '@/utils/formatters';

const PAGE_SIZE = 25;

function EmailBody({
  investorId,
  emailId,
}: {
  investorId: string;
  emailId: string;
}) {
  const { data: email, isLoading } = useInvestorEmail(investorId, emailId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!email) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Email not found.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b bg-muted/20 p-4">
        <h3 className="pr-8 text-base font-semibold leading-tight">
          {email.subject}
        </h3>
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {formatReferenceType(email.type)} · To: {email.to_email}
          </span>
          <DeliveryStatusBadge status={email.delivery_status} />
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="space-y-4 p-6">
          {email.attachments && email.attachments.length > 0 && (
            <div className="mx-auto max-w-[700px]">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                Attachments
              </p>
              <EmailAttachments attachments={email.attachments} />
            </div>
          )}
          {email.html_content ? (
            <div
              className="prose prose-sm mx-auto max-w-[700px] rounded-lg border bg-card p-6 dark:prose-invert"
              // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- email HTML is rendered from our own backend Jinja templates with autoescape
              dangerouslySetInnerHTML={{ __html: email.html_content }}
            />
          ) : (
            <p className="py-12 text-center text-muted-foreground">
              Rendered email content is not available.
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export function InvestorEmailsTab({ investorId }: { investorId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useInvestorEmails(investorId, {
    page,
    page_size: PAGE_SIZE,
  });
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Failed to load email history.
        </CardContent>
      </Card>
    );
  }

  const emails = data?.items ?? [];
  const totalCount = data?.total_count ?? 0;

  return (
    <div className="rounded-lg border bg-background shadow-sm">
      <div className="flex items-center gap-2 border-b bg-muted/40 p-4 text-sm font-medium">
        <span>Sent Emails</span>
        <span className="text-xs text-muted-foreground">
          {totalCount} total
        </span>
      </div>

      {emails.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 text-center text-muted-foreground">
          <div className="mb-4 rounded-full bg-primary/10 p-3">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <h3 className="mb-2 text-lg font-semibold">No emails sent yet</h3>
          <p className="max-w-sm text-sm">
            Business emails sent to this investor will appear here with their
            delivery status.
          </p>
        </div>
      ) : (
        <Table>
          <TableHeader className="sticky top-0 z-10 border-b bg-muted/40">
            <TableRow className="h-9 border-b-0 hover:bg-muted/40">
              <TableHead className="h-9 pl-6 text-xs font-medium">
                Subject
              </TableHead>
              <TableHead className="h-9 w-[180px] text-xs font-medium">
                Fund
              </TableHead>
              <TableHead className="h-9 w-[160px] text-xs font-medium">
                Type
              </TableHead>
              <TableHead className="h-9 w-[150px] text-xs font-medium">
                Status
              </TableHead>
              <TableHead className="h-9 w-[200px] pr-6 text-xs font-medium">
                Date
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {emails.map((email) => (
              <TableRow
                key={email.email_queue_id}
                onClick={() => setSelectedEmailId(email.email_queue_id)}
                className={cn(
                  'h-14 cursor-pointer border-b border-border/50 transition-colors hover:bg-muted/30'
                )}
              >
                <TableCell className="max-w-[400px] py-3 pl-6 text-sm font-medium">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate">{email.subject}</span>
                    {email.has_attachments && (
                      <Paperclip
                        className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground"
                        aria-label="Has attachments"
                      />
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-3 text-sm text-muted-foreground">
                  <span className="block truncate">
                    {email.fund_name ?? '-'}
                  </span>
                </TableCell>
                <TableCell className="py-3 text-sm text-muted-foreground">
                  {formatReferenceType(email.type)}
                </TableCell>
                <TableCell className="py-3">
                  <DeliveryStatusBadge status={email.delivery_status} />
                </TableCell>
                <TableCell className="py-3 pr-6 text-xs text-muted-foreground">
                  <div className="flex items-center">
                    <Clock className="mr-1 h-3 w-3" />
                    {formatDateTime(email.date)}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {totalCount > PAGE_SIZE && (
        <div className="border-t p-4">
          <TransactionPagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={setPage}
          />
        </div>
      )}

      <Dialog
        open={!!selectedEmailId}
        onOpenChange={(open) => !open && setSelectedEmailId(null)}
      >
        <DialogContent className="flex h-[85vh] flex-col gap-0 overflow-hidden rounded-lg border-0 p-0 sm:max-w-[750px]">
          {selectedEmailId && (
            <EmailBody investorId={investorId} emailId={selectedEmailId} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
